import React, { useState, useRef } from 'react';
import Icon from '../AppIcon';
import useLanguage from '../../hooks/useLanguage';

const NotificationsPanel = ({ isOpen, onClose, notifications = [], unreadCount = 0, onMarkAllRead, onMarkRead }) => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [isClosing, setIsClosing] = useState(false);
  const { t } = useLanguage();

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  if (!isOpen && !isClosing) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-200 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={handleClose}
      />
      {/* Panel - Wider */}
      <div className={`fixed right-4 top-20 w-[600px] max-w-[90vw] bg-card border border-border rounded-lg shadow-2xl z-50 transition-all duration-200 ${
        isClosing ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
      }`}>
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-bold text-foreground">{t('notifications')}</h3>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-md hover:bg-muted transition-colors"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Tabs - Styled like Positions Panel */}
        <div className="flex space-x-3 bg-gray-100 dark:bg-gray-800/50 p-2 mx-4 mt-4 rounded-lg">
          <button
            onClick={() => setActiveTab('support')}
            className={`flex items-center space-x-2 py-2.5 px-4 text-sm font-semibold rounded-md transition-all ${
              activeTab === 'support' 
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <Icon 
              name="Headphones" 
              size={18} 
              className={activeTab === 'support' ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'} 
            />
            <span>{t('support')}</span>
          </button>
          
          <button
            onClick={() => setActiveTab('announcement')}
            className={`flex items-center space-x-2 py-2.5 px-4 text-sm font-semibold rounded-md transition-all ${
              activeTab === 'announcement' 
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <Icon 
              name="Megaphone" 
              size={18} 
              className={activeTab === 'announcement' ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'} 
            />
            <span>{t('announcement')}</span>
          </button>
          
          <button
            onClick={() => setActiveTab('finance')}
            className={`flex items-center space-x-2 py-2.5 px-4 text-sm font-semibold rounded-md transition-all ${
              activeTab === 'finance' 
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <Icon 
              name="DollarSign" 
              size={18} 
              className={activeTab === 'finance' ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'} 
            />
            <span>{t('finance')}</span>
          </button>
        </div>

        {/* Content */}
        <div className="h-96 overflow-y-auto p-4">
          {/* Support Tab */}
          {activeTab === 'support' && (
            <div className="space-y-3">
              {notifications?.filter(n => n.type === 'support')?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-80 px-6">
                  <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
                    <Icon name="Headphones" size={32} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <h4 className="font-bold text-foreground mb-2">{t('noSupportMessages')}</h4>
                  <p className="text-sm text-muted-foreground text-center">{t('noSupportMessagesDesc')}</p>
                </div>
              ) : (
                notifications?.filter(n => n.type === 'support')?.map((notification) => (
                  <div
                    key={notification?.id}
                    className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-900/10 rounded-xl p-4 border border-blue-200 dark:border-blue-800 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => onMarkRead?.(notification?.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Icon name="Headphones" size={24} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h5 className="font-bold text-foreground">{notification?.title}</h5>
                          {!notification?.read && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse"></div>}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{notification?.message}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon name="Clock" size={12} />
                          <span>{notification?.timestamp ? new Date(notification?.timestamp)?.toLocaleString('tr-TR') : t('justNow')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Announcement Tab */}
          {activeTab === 'announcement' && (
            <div className="space-y-3">
              {notifications?.filter(n => n.type === 'announcement')?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-80 px-6">
                  <div className="w-20 h-20 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center mb-4">
                    <Icon name="Megaphone" size={32} className="text-orange-600 dark:text-orange-400" />
                  </div>
                  <h4 className="font-bold text-foreground mb-2">{t('noAnnouncements')}</h4>
                  <p className="text-sm text-muted-foreground text-center">{t('noAnnouncementsDesc')}</p>
                </div>
              ) : (
                notifications?.filter(n => n.type === 'announcement')?.map((notification) => (
                  <div
                    key={notification?.id}
                    className="bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-900/10 rounded-xl p-4 border border-orange-200 dark:border-orange-800 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => onMarkRead?.(notification?.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Icon name="Megaphone" size={24} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h5 className="font-bold text-foreground">{notification?.title}</h5>
                          <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 px-2 py-1 rounded-full font-medium">
                            {t('new')}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{notification?.message}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon name="Calendar" size={12} />
                          <span>{notification?.timestamp ? new Date(notification?.timestamp)?.toLocaleString('tr-TR') : t('justNow')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Finance Tab */}
          {activeTab === 'finance' && (
            <div className="space-y-3">
              {notifications?.filter(n => n.type === 'finance')?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-80 px-6">
                  <div className="w-20 h-20 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                    <Icon name="DollarSign" size={32} className="text-green-600 dark:text-green-400" />
                  </div>
                  <h4 className="font-bold text-foreground mb-2">{t('noFinanceMessages')}</h4>
                  <p className="text-sm text-muted-foreground text-center">{t('noFinanceMessagesDesc')}</p>
                </div>
              ) : (
                notifications?.filter(n => n.type === 'finance')?.map((notification) => (
                  <div
                    key={notification?.id}
                    className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-900/10 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => onMarkRead?.(notification?.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Icon name="DollarSign" size={24} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-bold text-foreground">{notification?.title}</h5>
                          <span className={`text-lg font-bold ${notification?.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {notification?.amount >= 0 ? '+' : ''}${notification?.amount?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{notification?.message}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Icon name="TrendingUp" size={12} />
                            <span>{notification?.timestamp ? new Date(notification?.timestamp)?.toLocaleString('tr-TR') : t('justNow')}</span>
                          </div>
                          <span className="text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 px-2 py-1 rounded font-medium">
                            {notification?.status || t('completed')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications?.length > 0 && unreadCount > 0 && (
          <div className="p-3 border-t border-gray-200 dark:border-border">
            <button
              onClick={onMarkAllRead}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
            >
              {t('markAllRead')}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationsPanel;